import type { FetchOptions } from 'ofetch'
import { omit } from 'es-toolkit'

type InputParams = Record<string, any> | null | undefined
export type ApiInstance = {
  defaults: {
    baseURL: () => string
  }
} & {
  [KGet in 'get']: <TOut = unknown>(url: string, options?: FetchOptions) => Promise<TOut>
} & {
  [KPost in 'post' | 'put' | 'patch' | 'delete']: <
    TOut = unknown,
    TIn extends InputParams = InputParams
  >(
    url: string,
    params?: TIn,
    options?: FetchOptions
  ) => Promise<TOut>
}

export function createUseApi(params: {
  getAuthHeader: () => string | undefined
  setAccessToken: (header: string) => void
  logout: () => void
}) {
  const defaults: Omit<FetchOptions, 'baseURL'> & { baseURL: () => string } = {
    baseURL: () => {
      const config = useRuntimeConfig()

      return config.public.baseApiUrl ?? 'https://api.thuisgekookt.nl/api'
    },
    onRequest: async ({ options }) => {
      options.headers = new Headers(options.headers)

      if (!options.headers.has('Accept')) {
        options.headers.set('Accept', 'application/json')
      }

      const authHeader = params.getAuthHeader()
      if (!options.headers.has('Authorization') && authHeader) {
        options.headers.set('Authorization', authHeader)
      }

      if (
        options.body &&
        typeof options.body === 'object' &&
        !(options.body instanceof FormData)
      ) {
        options.body = transformApiRequest(options.body)
      }
    },
    onResponse: async ({ response }) => {
      const headers = response.headers
      if (headers.get('content-type')?.includes('application/json')) {
        response._data = transformApiResponse(response._data)
      }
      checkForTokenRefresh(response.headers.get('authorization'), params.setAccessToken)
    },
    onResponseError: async ({ response }) => {
      await processApiError(response, params.setAccessToken, params.logout)
    }
  }

  const allowedMethods = new Set(['get', 'post', 'put', 'delete', 'patch', 'defaults'])

  return new Proxy<ApiInstance>({} as any, {
    get(_target: unknown, method: string): any {
      // Ignore symbols and Vue internals
      if (typeof method === 'symbol' || method.startsWith('__v')) {
        return undefined
      }

      if (!allowedMethods.has(method)) {
        if (import.meta.env.DEV) {
          console.warn(`Unknown API method: ${method}`)
        }
        return undefined
      }

      if (method === 'defaults') {
        return defaults
      }

      const httpMethod = method as 'get' | 'post' | 'put' | 'delete' | 'patch'
      return <TOut = unknown>(url: string, ...args: any[]) => {
        const [payloadOrOptions, maybeOptions] = args

        const baseURL = defaults.baseURL()
        const omittedDefaults = omit(defaults, ['baseURL'])

        if (httpMethod === 'get') {
          return $fetch<TOut>(url, {
            ...payloadOrOptions,
            method: httpMethod,
            baseURL: baseURL,
            ...omittedDefaults
          }) as Promise<TOut>
        } else {
          return $fetch<TOut>(url, {
            body: payloadOrOptions,
            ...maybeOptions,
            method: httpMethod,
            baseURL: baseURL,
            ...omittedDefaults
          })
        }
      }
    }
  })
}
